import PropTypes from 'prop-types';

/**
 * TextInput Component
 *
 * Reusable form input with Preline styling.
 * Supports text, textarea, labels, error messages, and required indicator.
 *
 * @param {Object} props
 * @param {string} props.label - Input label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message to display
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.multiline - Whether to render a textarea instead of input
 * @param {number} props.rows - Number of rows for textarea (default: 3)
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.type - Input type (default: 'text')
 * @param {boolean} props.disabled - Whether the input is disabled
 */
export function TextInput({
  label,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  multiline = false,
  rows = 3,
  name,
  id,
  type = 'text',
  disabled = false,
  list,
}) {
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');

  const baseClasses = `
    py-3 px-4 block w-full
    border-border-default rounded-lg text-sm
    focus:border-brand focus:ring-brand
    disabled:opacity-50 disabled:pointer-events-none
    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  const InputElement = multiline ? 'textarea' : 'input';

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-muted mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input or Textarea */}
      <InputElement
        id={inputId}
        name={name}
        type={multiline ? undefined : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={multiline ? rows : undefined}
        list={multiline ? undefined : list}
        className={baseClasses}
      />

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

TextInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  name: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  list: PropTypes.string,
};
